import React, { useState, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";
import { API, sendToEhpad } from "../services/api";
import EmailAutocomplete from "./EmailAutocomplete";
import { generateUuid } from "../utils/clientId";

function formatDateReception(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function patientSummary(commande) {
    const patientNames = (commande?.patients || [])
        .map(patient => [patient?.prenom, patient?.nom]
            .map(value => String(value || "").trim())
            .filter(Boolean)
            .join(" "))
        .filter(Boolean);

    if (patientNames.length === 0) return "";
    const label = patientNames.length === 1 ? "Patient concerné" : "Patients concernés";
    const displayedNames = patientNames.slice(0, 2).join(", ");
    return `${label} : ${displayedNames}${patientNames.length > 2 ? "..." : ""}`;
}

function commandeValidationPayload(commande) {
    return {
        id: commande.id,
        ehpad: commande.ehpad?.id ? { id: commande.ehpad.id } : null,
        patients: null,
    };
}

export default function ValiderCommandeModal({ commande, onClose }) {
    const { selectedPharmacy } = useAuth();
    const pharmacyName = selectedPharmacy?.name || "La Pharmacie";
    const dateReception = formatDateReception(commande.dateReception);
    const [recipients, setRecipients] = useState(() => {
        const contacts = commande.ehpad?.emailsContact ?? [];
        if (contacts.length > 0) return contacts;
        return commande.emailEmetteur ? [commande.emailEmetteur] : [];
    });
    const idempotencyKey = useMemo(() => generateUuid(), []);
    const patients = patientSummary(commande);

    const addRecipient = (email) => {
        if (!email || recipients.includes(email)) return;
        setRecipients(prev => [...prev, email]);
    };

    const removeRecipient = (index) => {
        setRecipients(prev => prev.filter((_, i) => i !== index));
    };

    const sujet = `Demande en préparation${commande.ehpad?.nom ? ` — ${commande.ehpad.nom}` : ""}`;
    const [contenu, setContenu] = useState(
        `Bonjour,\n\nNous accusons réception de votre demande du ${dateReception}.${patients ? `\n\n${patients}` : ""}\n\nCelle-ci est actuellement en cours de traitement. Nous reviendrons vers vous dans les meilleurs délais.\n\nBien cordialement,\n${pharmacyName}`
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleValidateOnly = async () => {
        setLoading(true);
        setError(null);
        try {
            await API.post(
                `/commandes/${commande.id}/valider`,
                commandeValidationPayload(commande),
                { noRedirectOn401: true }
            );
            onClose(true);
        } catch (e) {
            setError(e?.response?.status === 401
                ? "Session expirée. Veuillez vous reconnecter."
                : "Erreur lors de la validation.");
            setLoading(false);
        }
    };

    const handleValidateAndSend = async () => {
        setLoading(true);
        setError(null);
        try {
            await API.post(
                `/commandes/${commande.id}/valider`,
                commandeValidationPayload(commande),
                { noRedirectOn401: true }
            );
        } catch (e) {
            setError(e?.response?.status === 401
                ? "Session expirée. Veuillez vous reconnecter."
                : "Erreur lors de la validation.");
            setLoading(false);
            return;
        }
        try {
            await sendToEhpad(commande.id, sujet, contenu, recipients, [], idempotencyKey);
            onClose(true);
        } catch (e) {
            setError(e?.response?.status === 401
                ? "Session expirée. Veuillez vous reconnecter."
                : "Erreur lors de l'envoi du mail.");
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000
        }}>
            <div style={{
                background: "white",
                borderRadius: "12px",
                width: "540px",
                maxWidth: "95vw",
                maxHeight: "90vh",
                boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}>
                    <span style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}>
                        Valider la commande
                        {commande.ehpad?.nom && (
                            <span style={{ fontWeight: 400, color: "#64748b", marginLeft: "6px" }}>
                                — {commande.ehpad.nom}
                            </span>
                        )}
                    </span>
                    <button
                        onClick={() => onClose(false)}
                        disabled={loading}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#94a3b8", lineHeight: 1 }}
                    >✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "14px", overflowY: "auto" }}>

                    {/* Destinataires */}
                    <div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>
                            Destinataire(s) du mail
                        </div>
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                            alignItems: "center",
                            border: "1px solid #cbd5e1",
                            borderRadius: "7px",
                            padding: "6px 8px",
                            minHeight: "40px",
                        }}>
                            {recipients.map((r, i) => (
                                <span key={i} style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                    borderRadius: "4px",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    color: "#1e40af",
                                }}>
                                    {r}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeRecipient(i); }}
                                        disabled={loading}
                                        style={{ background: "none", border: "none", cursor: "pointer", color: "#93c5fd", fontSize: "14px", lineHeight: 1, padding: 0 }}
                                    >×</button>
                                </span>
                            ))}
                            <EmailAutocomplete
                                onSelect={addRecipient}
                                exclude={recipients}
                                placeholder={recipients.length === 0 ? "Ajouter un destinataire..." : ""}
                            />
                        </div>
                        {recipients.length === 0 && (
                            <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#f59e0b" }}>
                                Aucun destinataire — seule la validation sans envoi est disponible.
                            </p>
                        )}
                    </div>

                    {/* Corps du message */}
                    <div>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>
                            Message
                        </div>
                        <textarea
                            value={contenu}
                            onChange={e => setContenu(e.target.value)}
                            rows={7}
                            disabled={loading}
                            style={{
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: "7px",
                                border: "1px solid #cbd5e1",
                                fontSize: "13px",
                                resize: "vertical",
                                outline: "none",
                                color: "#1e293b",
                                lineHeight: "1.6",
                                fontFamily: "inherit",
                                boxSizing: "border-box",
                                background: loading ? "#f8fafc" : "white"
                            }}
                        />
                    </div>

                    {error && (
                        <div style={{ color: "#dc2626", fontSize: "13px" }}>{error}</div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: "12px 20px",
                    borderTop: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px"
                }}>
                    <button
                        onClick={() => onClose(false)}
                        disabled={loading}
                        style={{
                            padding: "7px 16px",
                            borderRadius: "7px",
                            border: "1px solid #cbd5e1",
                            background: "white",
                            color: "#475569",
                            fontSize: "13px",
                            cursor: loading ? "default" : "pointer"
                        }}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleValidateOnly}
                        disabled={loading}
                        style={{
                            padding: "7px 16px",
                            borderRadius: "7px",
                            border: "1px solid #16a34a",
                            background: "white",
                            color: "#16a34a",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: loading ? "default" : "pointer",
                            opacity: loading ? 0.6 : 1
                        }}
                    >
                        {loading ? "…" : "Valider sans envoyer"}
                    </button>
                    <button
                        onClick={handleValidateAndSend}
                        disabled={loading || recipients.length === 0}
                        style={{
                            padding: "7px 16px",
                            borderRadius: "7px",
                            border: "none",
                            background: (loading || recipients.length === 0) ? "#86efac" : "#16a34a",
                            color: "white",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: (loading || recipients.length === 0) ? "not-allowed" : "pointer"
                        }}
                    >
                        {loading ? "…" : "Valider et envoyer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
